<template>
<div class="blog-header">
    <div class="blog-header-content">
        <p v-t="'BLOG.TITLE1'" class="top-title" />
        <h1 v-t="'BLOG.TITLE2'" />
    </div>
</div>
<div class="subhead">
    <h1>
        <NuxtLink :to="localeRoute('/blog/year')">Blog Posts Through the Years</NuxtLink>: {{ route?.params?.yearslug }}
    </h1>
</div>
<div class="yearly-posts">
    <NuxtLink v-for="(post) in posts" :key="post.slug" :to="localeRoute(`/blog/${post.slug}`)">
        <div class="year-post">
            <div class="title">{{ post.title }}</div>
            <div class="date">{{ dateString(post.publishedAt) }}</div>
        </div>
    </NuxtLink>
</div>
</template>

<script setup lang="ts">

import type { CMS } from '$plugins/cms'
import { JBGWeb } from '$types/JBGWeb'

const i18n = useI18n()
const app = useNuxtApp()
const localeRoute = useLocaleRoute()
const route = useRoute()

const dateString = (postDate: string) => {
    const d = new Date(postDate)
    return d.toLocaleDateString(i18n.locale.value, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const { data: posts } = await useAsyncData<JBGWeb.BlogPost[]>(
    `year-posts-${route?.params?.yearslug}`,
    async () => {
        try {
            const options: (CMS.FetchOptions & { withMeta?: false | undefined; }) = {
                fields: ['slug', 'title', 'publishedAt'],
                sort: 'publishedAt:desc',
                pagination: {
                    page: 1,
                    pageSize: 200
                },
                locale: i18n.locale.value,
                filters: {
                    '$and][0][publishedAt][$gte': `${route.params.yearslug}-01-01`,
                    '$and][1][publishedAt][$lte': `${route.params.yearslug}-12-31`
                }
            }
            const blogs = await app.$cms.fetchMany<JBGWeb.BlogPost[]>('blog-posts', options)
            return blogs
        } catch (error) {
            console.error('error loading blog posts')
            throw error
        }
    }
)

</script>

<style lang="scss">
@use "$styles/kit.scss" as *;

$overlayGradient: linear-gradient(0deg,
    rgba(0,0,0,0) 30%,
    rgba(0,0,0,.75) 100%);

.blog-header {
    background-image: url("/images/blog-header.jpg");
    background-size: cover;
    background-position: center;
    height: 400px;
    margin-top: -88px;
    margin-bottom: 30px;
    padding-top: 100px;
    display: flex;
    @include mq-small-and-less {
        height: 300px;
    }

    &::after { // overlay
        content: '';
        z-index: 1;
        position: absolute;
        top: 0;
        left: 50%;
        width: 100%;
        height: 400px;
        background: $overlayGradient;
        transform: translateX(-50%);
        @include mq-small-and-less {
            height: 300px;
        }

    }

    .blog-header-content {
        display: inline-block;
        z-index: 2;
        text-align: center;
        width: 100%;
        margin-top: auto;
        margin-bottom: auto;
        padding-bottom: 50px;
        text-shadow: 0px 0px 10px #000000a0;
        h1, .top-title {
            text-transform: uppercase;
            margin: 0;
        }
        h1 {
            font-size: 72px;
            margin: 0;
        }
        .top-title {
            font-size: 40px;
            font-weight: bold;
        }
        @include mq-small-and-less {
            h1 { font-size: 40px; }
            .top-title { font-size: 25px; }
        }
    }
}

.subhead {
    text-align: center;
    margin-bottom: 50px;
}

.yearly-posts {
    max-width: 900px;
    margin: auto;
    display: flex;
    gap: 20px;
    flex-direction: column;
    .year-post {
        display: grid;
        grid-template-columns: 2fr, 1fr;

        .title {
            font-weight: 800;
        }
        .date {
            font-size: 12px;
        }
    }
}


</style>
